import { useState } from "react";
import {
  ArrowUpRight, ArrowDownRight,
  Shield, Landmark, Zap, Leaf, Moon
} from "lucide-react";

type ThemeId = "corporate-blue" | "dark-finance" | "emerald-trust" | "minimal-mono" | "navy-gold";

interface Theme {
  id: ThemeId;
  name: string;
  description: string;
  icon: React.ReactNode;
  colors: {
    background: string;
    card: string;
    cardBorder: string;
    primary: string;
    primaryForeground: string;
    accent: string;
    muted: string;
    mutedForeground: string;
    success: string;
    text: string;
    textSecondary: string;
  };
}

const themes: Theme[] = [
  {
    id: "corporate-blue",
    name: "Corporate Blue",
    description: "Azul corporativo clássico. Transmite confiança institucional, usado por grandes bancos como Itaú e Caixa.",
    icon: <Landmark className="w-5 h-5" />,
    colors: {
      background: "#F0F4F8",
      card: "#FFFFFF",
      cardBorder: "#E2E8F0",
      primary: "#1E40AF",
      primaryForeground: "#FFFFFF",
      accent: "#3B82F6",
      muted: "#F1F5F9",
      mutedForeground: "#64748B",
      success: "#059669",
      text: "#0F172A",
      textSecondary: "#475569",
    },
  },
  {
    id: "dark-finance",
    name: "Dark Finance",
    description: "Modo escuro premium. Transmite tecnologia e modernidade, usado por fintechs como Nubank e corretoras.",
    icon: <Moon className="w-5 h-5" />,
    colors: {
      background: "#0A0A0F",
      card: "#12121A",
      cardBorder: "#1E1E2E",
      primary: "#8B5CF6",
      primaryForeground: "#FFFFFF",
      accent: "#A78BFA",
      muted: "#1E1E2E",
      mutedForeground: "#71717A",
      success: "#22C55E",
      text: "#FAFAFA",
      textSecondary: "#A1A1AA",
    },
  },
  {
    id: "emerald-trust",
    name: "Emerald Trust",
    description: "Verde esmeralda sofisticado. Transmite crescimento e prosperidade, associado a investimentos.",
    icon: <Leaf className="w-5 h-5" />,
    colors: {
      background: "#F8FAFC",
      card: "#FFFFFF",
      cardBorder: "#E2E8F0",
      primary: "#047857",
      primaryForeground: "#FFFFFF",
      accent: "#10B981",
      muted: "#F0FDF4",
      mutedForeground: "#64748B",
      success: "#059669",
      text: "#0F172A",
      textSecondary: "#475569",
    },
  },
  {
    id: "minimal-mono",
    name: "Minimal Mono",
    description: "Minimalista monocromático. Transmite seriedade e foco, usado por plataformas como Bloomberg.",
    icon: <Zap className="w-5 h-5" />,
    colors: {
      background: "#FAFAFA",
      card: "#FFFFFF",
      cardBorder: "#E5E5E5",
      primary: "#171717",
      primaryForeground: "#FFFFFF",
      accent: "#404040",
      muted: "#F5F5F5",
      mutedForeground: "#737373",
      success: "#16A34A",
      text: "#0A0A0A",
      textSecondary: "#525252",
    },
  },
  {
    id: "navy-gold",
    name: "Navy & Gold",
    description: "Azul marinho com dourado. Transmite luxo e exclusividade, usado por private banking.",
    icon: <Shield className="w-5 h-5" />,
    colors: {
      background: "#0C1222",
      card: "#141D2F",
      cardBorder: "#1E2A45",
      primary: "#D4AF37",
      primaryForeground: "#0C1222",
      accent: "#F4D03F",
      muted: "#1A2744",
      mutedForeground: "#8B9DC3",
      success: "#4ADE80",
      text: "#F8FAFC",
      textSecondary: "#94A3B8",
    },
  },
];

function DashboardPreview({ theme }: { theme: Theme }) {
  const c = theme.colors;

  return (
    <div
      className="rounded-xl overflow-hidden border-2 transition-all"
      style={{
        backgroundColor: c.background,
        borderColor: c.cardBorder,
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between"
        style={{ backgroundColor: c.card, borderColor: c.cardBorder }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
            style={{ backgroundColor: c.primary, color: c.primaryForeground }}
          >
            SB
          </div>
          <span style={{ color: c.text }} className="font-semibold text-sm">BRLD</span>
        </div>
        <div
          className="px-2 py-1 rounded text-xs font-medium"
          style={{ backgroundColor: c.muted, color: c.mutedForeground }}
        >
          Admin
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2">
          <div
            className="p-3 rounded-lg border"
            style={{ backgroundColor: c.card, borderColor: c.cardBorder }}
          >
            <p className="text-xs mb-1" style={{ color: c.mutedForeground }}>Saldo BRLD</p>
            <p className="font-bold text-lg" style={{ color: c.primary }}>R$ 1,2M</p>
          </div>
          <div
            className="p-3 rounded-lg border"
            style={{ backgroundColor: c.card, borderColor: c.cardBorder }}
          >
            <p className="text-xs mb-1" style={{ color: c.mutedForeground }}>Rendimento</p>
            <p className="font-bold text-lg" style={{ color: c.success }}>+R$ 12k</p>
          </div>
          <div
            className="p-3 rounded-lg border"
            style={{ backgroundColor: c.card, borderColor: c.cardBorder }}
          >
            <p className="text-xs mb-1" style={{ color: c.mutedForeground }}>Lastro</p>
            <p className="font-bold text-lg" style={{ color: c.text }}>100%</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            className="flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
            style={{ backgroundColor: c.primary, color: c.primaryForeground }}
          >
            <ArrowDownRight className="w-4 h-4" />
            Depositar
          </button>
          <button
            className="flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1 border"
            style={{ backgroundColor: "transparent", color: c.text, borderColor: c.cardBorder }}
          >
            <ArrowUpRight className="w-4 h-4" />
            Resgatar
          </button>
        </div>

        {/* Table Preview */}
        <div
          className="rounded-lg border overflow-hidden"
          style={{ backgroundColor: c.card, borderColor: c.cardBorder }}
        >
          <div
            className="px-3 py-2 border-b text-xs font-medium"
            style={{ backgroundColor: c.muted, borderColor: c.cardBorder, color: c.mutedForeground }}
          >
            Empresas Recentes
          </div>
          {[
            { name: "Empresa ABC", status: "Ativo", statusColor: c.success },
            { name: "Tech Corp", status: "Pendente", statusColor: c.accent },
          ].map((item, i) => (
            <div
              key={i}
              className="px-3 py-2 flex items-center justify-between border-b last:border-0"
              style={{ borderColor: c.cardBorder }}
            >
              <span className="text-sm" style={{ color: c.text }}>{item.name}</span>
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${item.statusColor}20`, color: item.statusColor }}
              >
                {item.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DesignShowcasePage() {
  const [selectedTheme, setSelectedTheme] = useState<ThemeId>("corporate-blue");

  const currentTheme = themes.find(t => t.id === selectedTheme)!;

  return (
    <div className="min-h-screen bg-slate-100 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">Design System - stableBRL</h1>
          <p className="text-slate-600">
            Escolha o tema que melhor representa a identidade do produto
          </p>
        </div>

        {/* Theme Selector */}
        <div className="flex flex-wrap justify-center gap-3">
          {themes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setSelectedTheme(theme.id)}
              className={`px-4 py-2 rounded-lg border-2 flex items-center gap-2 transition-all ${
                selectedTheme === theme.id
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
            >
              {theme.icon}
              <span className="font-medium">{theme.name}</span>
            </button>
          ))}
        </div>

        {/* Selected Theme Info */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: currentTheme.colors.primary }}
            >
              <div style={{ color: currentTheme.colors.primaryForeground }}>
                {currentTheme.icon}
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900">{currentTheme.name}</h2>
              <p className="text-slate-600 mt-1">{currentTheme.description}</p>

              {/* Color Palette */}
              <div className="flex gap-2 mt-4">
                {[
                  { label: "Primary", color: currentTheme.colors.primary },
                  { label: "Background", color: currentTheme.colors.background },
                  { label: "Card", color: currentTheme.colors.card },
                  { label: "Accent", color: currentTheme.colors.accent },
                  { label: "Success", color: currentTheme.colors.success },
                  { label: "Text", color: currentTheme.colors.text },
                ].map((c) => (
                  <div key={c.label} className="text-center">
                    <div
                      className="w-10 h-10 rounded-lg border border-slate-200 shadow-sm"
                      style={{ backgroundColor: c.color }}
                    />
                    <span className="text-xs text-slate-500 mt-1 block">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* All Themes Grid */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Comparativo de Todos os Temas</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {themes.map((theme) => (
              <div key={theme.id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded flex items-center justify-center"
                      style={{ backgroundColor: theme.colors.primary, color: theme.colors.primaryForeground }}
                    >
                      {theme.icon}
                    </div>
                    <span className="font-medium text-slate-900">{theme.name}</span>
                  </div>
                  {selectedTheme === theme.id && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      Selecionado
                    </span>
                  )}
                </div>
                <div
                  className={`cursor-pointer transition-all ${
                    selectedTheme === theme.id ? "ring-2 ring-blue-500 ring-offset-2" : ""
                  }`}
                  onClick={() => setSelectedTheme(theme.id)}
                >
                  <DashboardPreview theme={theme} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendation */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Recomendação para stableBRL
          </h3>
          <div className="mt-4 space-y-3 text-slate-700">
            <p>
              <strong>Para um produto B2B de gestão de caixa empresarial</strong>, recomendo considerar:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong>Corporate Blue</strong> - Se o público são empresas tradicionais (indústria, varejo).
                Transmite solidez e é familiar para CFOs e tesoureiros.
              </li>
              <li>
                <strong>Navy & Gold</strong> - Se quer posicionar como premium/high-end.
                Ideal para grandes empresas e family offices.
              </li>
              <li>
                <strong>Dark Finance</strong> - Se o público são empresas de tech ou fintechs.
                Moderno e diferenciado, mas pode não agradar empresas tradicionais.
              </li>
            </ul>
            <p className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
              <strong>Minha sugestão:</strong> Comece com <strong>Corporate Blue</strong> (mais seguro e universal)
              com a opção de dark mode usando <strong>Navy & Gold</strong> ou <strong>Dark Finance</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
